/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module IPReputationDBModule
 */

import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';
import { IIPReputationServiceStatus } from 'generated-types';

interface IIPReputationDB {
    base_file_refs?: string[];
    base_file_v6_refs?: string[];
    incremental_file_refs?: string[];
    incremental_file_v6_refs?: string[];
    service_status?: IIPReputationServiceStatus;
}

interface IIPReputationDBData {
    config: IIPReputationDB;
}

export enum IPReputationDBStatus {
    ACTIVE = 'ACTIVE',
    ERROR = 'ERROR',
}

const enum FileRefTypeKey {
    BASEIPv4 = 'baseIpv4',
    BASEIPv6 = 'baseIpv6',
    INCREMENTALIPv4 = 'incrementalIpv4',
    INCREMENTALIPv6 = 'incrementalIpv6',
}

const fileRefType = {
    [FileRefTypeKey.BASEIPv4]: 'Base IPv4',
    [FileRefTypeKey.BASEIPv6]: 'Base IPv6',
    [FileRefTypeKey.INCREMENTALIPv4]: 'Incremental IPv4',
    [FileRefTypeKey.INCREMENTALIPv6]: 'Incremental IPv6',
};

export interface IIPReputationDBFile {
    type: typeof fileRefType[keyof typeof fileRefType];
    ref: string;
}

/**
 * @description IP Reputation DB item.
 * @author Aravindh Nagarajan, alextsg
 */
export class IPReputationDB extends ObjectTypeItem {
    public data: IIPReputationDBData;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'IPReputationDB',
            permissionName: 'PERMISSION_IPREPUTATIONDB',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Returns the error status of the IP Reputation DB item. If the error is 'SUCCESS' then there
     * is no error.
     */
    public get status(): IPReputationDBStatus {
        return this.serviceStatusError === 'SUCCESS' ?
            IPReputationDBStatus.ACTIVE :
            IPReputationDBStatus.ERROR;
    }

    /**
     * Returns any error the IP Reputation DB service status might have.
     */
    public get serviceStatusError(): string {
        return this.config.service_status?.config.error;
    }

    /**
     * Returns a list of files belonging to the IP Reputation DB item. Both Base and Incremental
     * files are combined into the returned list, as objects with a type property describing the
     * file type.
     */
    public get files(): IIPReputationDBFile[] {
        const files: IIPReputationDBFile[] = [];
        const {
            base_file_refs: baseFileRefsIPv4 = [],
            base_file_v6_refs: baseFileRefsIPv6 = [],
            incremental_file_refs: incrementalFileRefsIPv4 = [],
            incremental_file_v6_refs: incrementalFileRefsIPv6 = [],
        } = this.config;

        [{
            refs: baseFileRefsIPv4,
            type: FileRefTypeKey.BASEIPv4,
        }, {
            refs: baseFileRefsIPv6,
            type: FileRefTypeKey.BASEIPv6,
        }, {
            refs: incrementalFileRefsIPv4,
            type: FileRefTypeKey.INCREMENTALIPv4,
        }, {
            refs: incrementalFileRefsIPv6,
            type: FileRefTypeKey.INCREMENTALIPv6,
        }].forEach(({ refs, type }) => {
            refs.forEach((ref: string) => files.push({
                type,
                ref,
            }));
        });

        return files;
    }

    /**
     * Returns the last time the IPReputationDB was updated in seconds.
     */
    public get lastUpdate(): number {
        return this.config.service_status?.config.last_successful_update_check?.config.usecs;
    }
}

Object.assign(IPReputationDB.prototype, {
    objectName: 'ipreputationdb',
});
